import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createStocktakeRpc } from '@/services/stocktake';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { ClipboardList, Loader2, X } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateStocktakeModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [itemType, setItemType] = useState<'all' | 'laptops' | 'accessories'>('all');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createStocktakeRpc({ title: title.trim(), item_type: itemType, notes: notes.trim() || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stocktakes'] });
      setTitle('');
      setItemType('all');
      setNotes('');
      setError(null);
      onClose();
    },
    onError: (err: unknown) => setError(parseDatabaseError(err)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Stocktake title is required.');
      return;
    }
    mutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-brand-border">
          <h2 className="text-sm font-bold text-brand-black flex items-center space-x-2">
            <ClipboardList className="w-4 h-4 text-brand-green" />
            <span>New Stocktake</span>
          </h2>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-black">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 text-xs">
          {error && <ErrorAlert message={error} />}

          <div>
            <label className="block font-bold text-brand-black uppercase mb-1">
              Title <span className="text-brand-red">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. August 2026 Full Stocktake"
              className="w-full px-3 py-1.5 border border-brand-border rounded focus:ring-2 focus:ring-brand-green"
              autoFocus
            />
          </div>

          <div>
            <label className="block font-bold text-brand-black uppercase mb-1">Item Type</label>
            <select
              value={itemType}
              onChange={(e) => setItemType(e.target.value as 'all' | 'laptops' | 'accessories')}
              className="w-full px-3 py-1.5 border border-brand-border rounded bg-white font-semibold"
            >
              <option value="all">All Items (Laptops + Accessories)</option>
              <option value="laptops">Laptops Only</option>
              <option value="accessories">Accessories Only</option>
            </select>
          </div>

          <div>
            <label className="block font-semibold text-brand-black mb-1">Notes (Optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this stocktake..."
              className="w-full px-3 py-1.5 border border-brand-border rounded resize-none"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-2 border-t border-brand-border">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="px-4 py-1.5 font-semibold text-brand-muted hover:text-brand-black"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !title.trim()}
              className="inline-flex items-center space-x-1.5 px-4 py-1.5 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover disabled:opacity-50"
            >
              {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
              <span>Create Stocktake</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
